import { useCallback, useMemo, useState } from 'react';

interface UseSelectedResourceIdsReturn {
  selectedResourceIds: Set<number>;
  toggleResourceId: (id: number) => void;
  isResourceIdSelected: (id: number) => boolean;
  selectedCount: number;
  resetSelectedResourceIds: () => void;
  selectAllResourceIds: (ids: number[]) => void;
  deselectAllResourceIds: () => void;
}

export function useSelectedResourceIds(): UseSelectedResourceIdsReturn {
  const [selectedResourceIds, setSelectedResourceIds] = useState<Set<number>>(new Set<number>());

  const selectedCount = useMemo(() => selectedResourceIds.size, [selectedResourceIds]);

  const toggleResourceId = useCallback((id: number) => {
    setSelectedResourceIds((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  }, []);

  const isResourceIdSelected = useCallback(
    (id: number): boolean => {
      return selectedResourceIds.has(id);
    },
    [selectedResourceIds],
  );

  const resetSelectedResourceIds = useCallback(() => {
    setSelectedResourceIds(new Set<number>());
  }, []);

  const selectAllResourceIds = useCallback((ids: number[]) => {
    setSelectedResourceIds(new Set(ids));
  }, []);

  const deselectAllResourceIds = useCallback(() => {
    setSelectedResourceIds(new Set<number>());
  }, []);

  return {
    selectedResourceIds,
    toggleResourceId,
    isResourceIdSelected,
    selectedCount,
    resetSelectedResourceIds,
    selectAllResourceIds,
    deselectAllResourceIds,
  };
}
